import { useEffect, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import { useConfig } from '@/hooks/useConfig'
import { WRKS_PORTAL_URLS } from '@/lib/constants'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'

/**
 * 개발서버 여부를 판별
 * 1) URL에 devMode=true 파라미터가 있는 경우 (token-extension 로컬환경 바로가기)
 * 2) document.referrer 호스트가 ai3.wrks.ai인 경우 (SSO 리다이렉트)
 */
function detectDevMode(params: URLSearchParams): boolean {
  if (params.get('devMode') === 'true') {
    return true
  }
  try {
    return new URL(document.referrer).hostname === 'ai3.wrks.ai'
  } catch {
    return false
  }
}

/**
 * Wrks SSO 콜백 페이지 (레거시 진입 모드)
 * URL: /auth?wrksToken=...&source=...
 * 웍스에서 이 URL로 리다이렉트됨. 포탈 진입 모드가 `authorize` 로 바뀌면 등록된 앱 URL(…/auth)이
 * 파라미터 없이 열릴 수 있으므로, 그 경우는 홈으로 보내 OAuth 자동 시도에 맡긴다.
 * 다른 배포의 계정이면(예: gov 계정이 apps.wrks.ai 로 진입) 백엔드가 redirect 를 돌려주고,
 * loginWithWrks 는 그 배포로 넘긴 뒤 끝나지 않는다: 홈으로 가면 OAuth 자동 시도가 그 이동을
 * 취소하기 때문이다 (lib/handoff.ts).
 */
export function WrksSSOCallback() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { loginWithWrks } = useAuthStore()
  const { data: config } = useConfig()
  // message가 null이면 렌더 시 폴백 문구(pages.ssoAuthFailed)를 사용한다
  const [loginError, setLoginError] = useState<{ message: string | null } | null>(null)
  const wrksToken = searchParams.get('wrksToken')
  const source = searchParams.get('source') as 'gov' | 'wrks' | null
  // 진입 URL 의 source 가 사용자의 웍스다: 다른 배포로 잘못 들어온 gov 사용자도 gov 웍스로 돌려보낸다
  const portalUrl =
    source === 'gov' || source === 'wrks'
      ? WRKS_PORTAL_URLS[source]
      : (config?.loginUrl ?? WRKS_PORTAL_URLS.wrks)

  // 번역 키를 보관해 언어 변경 시에도 effect가 재실행되지 않도록 한다 (렌더 시 t()로 번역)
  const paramErrorKey =
    !wrksToken || !source
      ? 'pages.ssoInvalidAccess'
      : source !== 'gov' && source !== 'wrks'
        ? 'pages.ssoInvalidSource'
        : null

  useEffect(() => {
    if (paramErrorKey || !wrksToken || !source) {
      return
    }

    // 백엔드에 wrksToken 전달 → 백엔드가 DB에 저장
    // referrer 또는 devMode 파라미터로 개발서버 여부 판별
    loginWithWrks(wrksToken, source, detectDevMode(searchParams))
      .then(() => {
        navigate('/', { replace: true })
      })
      .catch((err) => {
        // authStore가 던지는 메시지는 이미 현재 언어로 번역되어 있어 그대로 표시한다
        setLoginError({
          message: err instanceof Error && err.message ? err.message : null,
        })
      })
  }, [paramErrorKey, wrksToken, source, searchParams, loginWithWrks, navigate])

  if (!wrksToken && !source) {
    return <Navigate to="/" replace />
  }

  if (paramErrorKey || loginError) {
    const message = paramErrorKey
      ? t(paramErrorKey)
      : (loginError?.message ?? t('pages.ssoAuthFailed'))

    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-surface">
        <div className="text-center space-y-4 p-8">
          <div className="text-destructive text-lg font-medium">{message}</div>
          <p className="text-caption">
            {t('pages.ssoContactAdmin')}
          </p>
          <Button onClick={() => window.location.assign(portalUrl)} variant="outline">
            {t('pages.ssoBackToWrks')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-surface">
      <div className="text-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
        <p className="text-body">{t('pages.ssoAuthenticating')}</p>
      </div>
    </div>
  )
}
